import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Loader2, Check, Instagram, Facebook, MessageCircle, X, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import ContactQuickActions from '@/components/contact/ContactQuickActions';
import GoogleMapEmbed from '@/components/contact/GoogleMapEmbed';
import { useLanguage } from '@/i18n/LanguageProvider';

const offices = [
  {
    city: 'Palermo',
    address: 'Viale Regione Siciliana Nord Ovest 2132',
    zip: 'Palermo 90135'
  }
];

export default function Contact() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    profession: '',
    subject: '',
    message: ''
  });
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const submitMutation = useMutation({
    mutationFn: (data) => base44.entities.ContactSubmission.create(data),
    onSuccess: () => {
      setSubmitted(true);
      setFormData({ name: '', email: '', profession: '', subject: '', message: '' });
      setAgreed(false);
    },
    onError: () => {
      toast.error('Failed to send message. Please try again.');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error('Please accept the privacy policy');
      return;
    }
    submitMutation.mutate(formData);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <>
      <SEO 
        title={t('Contatti')}
        description={t('Contatta Bellomonte & Pensabene per il tuo progetto di architettura o interior design. Studio a Palermo, Viale Regione Siciliana Nord Ovest 2132.')}
        keywords="contatti architettura Palermo, preventivo ristrutturazione, consulenza design, architetto Palermo"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: t('Contatti'),
          url: 'https://studioarchitetturabellomontepensabene.it/Contact'
        }}
      />
      <div className="pb-14 pt-20 md:pb-24 md:pt-32">
        <div className="mx-auto max-w-[1800px] px-6 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 md:mb-20"
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light text-[#2d2d2d] mb-8">
            {t('Contatti')}
          </h1>
          <div className="w-16 h-px bg-[#74675a]" />
        </motion.div>

        <div className="mb-10 lg:hidden"><ContactQuickActions /></div>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-24">
        {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="font-serif text-2xl md:text-3xl text-[#2d2d2d] mb-8">
              Bellomonte & Pensabene
            </h2>

            <div className="space-y-6 mb-12">
              <a 
                href="mailto:rosariopensabenearc@yahoo.it"
                className="flex items-center gap-3 text-[#5f5f5f] hover:text-[#2d2d2d] transition-colors"
              >
                <Mail size={18} />
                rosariopensabenearc@yahoo.it
              </a>
              <a 
                href="tel:+393493810464"
                className="flex items-center gap-3 text-[#5f5f5f] hover:text-[#2d2d2d] transition-colors"
              >
                <Phone size={18} />
                +39 349 381 0464
              </a>
              <p className="flex items-start gap-3 text-[#5f5f5f] text-sm">
                <Clock size={18} className="mt-0.5 flex-shrink-0" />
                {t('Dal lunedì al venerdì dalle 9:00 – 19:00')}
              </p>
              <Button
                onClick={() => setShowQR(true)}
                className="hidden items-center gap-2 bg-[#25D366] text-white hover:bg-[#20BA5A] lg:flex"
              >
                <MessageCircle size={18} />
                {t('Contattaci su WhatsApp')}
              </Button>
            </div>

            <div className="border-t border-[#e5e5e5] pt-8 space-y-8">
              {offices.map((office) => (
                <div key={office.city}>
                  <h3 className="text-xs tracking-widest-custom uppercase text-[#74675a] mb-3">
                    {office.city}
                  </h3>
                  <div className="flex items-start gap-3 text-[#5f5f5f]">
                    <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p>{office.address}</p>
                      <p>{office.zip}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8"><GoogleMapEmbed /></div>

            {/* Social Section */}
            <div className="border-t border-[#e5e5e5] pt-8 mt-8">
              <h3 className="text-xs tracking-widest-custom uppercase text-[#74675a] mb-4">
                {t('Seguici')}
              </h3>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/bellomonte_pensabene/"
                  aria-label="Instagram — Bellomonte & Pensabene"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border border-[#e5e5e5] flex items-center justify-center 
                    text-[#5f5f5f] hover:text-[#2d2d2d] hover:border-[#2d2d2d] transition-all duration-300"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://www.facebook.com/bellomontepensabene"
                  aria-label="Facebook — Bellomonte & Pensabene"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border border-[#e5e5e5] flex items-center justify-center 
                    text-[#5f5f5f] hover:text-[#2d2d2d] hover:border-[#2d2d2d] transition-all duration-300"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://www.tiktok.com/@bellomonte_pensabene"
                  aria-label="TikTok — Bellomonte & Pensabene"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border border-[#e5e5e5] flex items-center justify-center 
                    text-[#5f5f5f] hover:text-[#2d2d2d] hover:border-[#2d2d2d] transition-all duration-300"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Careers Section */}
            <div className="border-t border-[#e5e5e5] pt-8 mt-8">
              <h3 className="font-serif text-xl text-[#2d2d2d] mb-4">
                {t('Lavora con noi')}
              </h3>
              <p className="text-[#5f5f5f] text-sm leading-relaxed mb-4">
                {t('"NO BORING PEOPLE" — Lo studio è sempre alla ricerca di persone talentuose e creative.')}
              </p>
              <p className="text-[#5f5f5f] text-sm">
                Invia un unico file PDF di max 5 MB con cover letter, CV e alcuni esempi di tuoi lavori a{' '}
                <a href="mailto:rosariopensabenearc@yahoo.it" className="text-[#74675a] hover:underline">
                  rosariopensabenearc@yahoo.it
                </a>
              </p>
            </div>
          </motion.div>

          {/* WhatsApp QR Code Modal */}
          <AnimatePresence>
            {showQR && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                onClick={() => setShowQR(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white rounded-lg p-8 max-w-sm w-full relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setShowQR(false)}
                    aria-label={t('Chiudi menu')}
                    className="absolute top-4 right-4 text-[#5f5f5f] hover:text-[#2d2d2d] transition-colors"
                  >
                    <X size={24} />
                  </button>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    
                    <h3 className="font-serif text-2xl text-[#2d2d2d] mb-2">
                      {t('Scansiona il QR Code')}
                    </h3>
                    
                    <p className="text-[#5f5f5f] text-sm mb-6">
                      {t('Inquadra con la fotocamera per aprire WhatsApp')}
                    </p>
                    
                    <div className="bg-white p-4 rounded-lg border-2 border-[#e5e5e5] inline-block">
                      <img 
                        src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/696b8a6ae79eec44c999be35/cdfe383c2_frame.png"
                        alt="WhatsApp QR Code"
                        width="256"
                        height="256"
                        loading="lazy"
                        decoding="async"
                        className="w-64 h-64 object-contain"
                      />
                    </div>
                    
                    <p className="text-[#5f5f5f] text-xs mt-4">
                      +39 349 381 0464
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="font-serif text-2xl md:text-3xl text-[#2d2d2d] mb-8">
              {t('Richiedi un preventivo')}
            </h2>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#f8f8f8] rounded-lg p-8 text-center"
              >
                <div className="w-16 h-16 bg-[#74675a]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-[#74675a]" />
                </div>
                <h3 className="font-serif text-xl text-[#2d2d2d] mb-2">{t('Grazie!')}</h3>
                <p className="text-[#5f5f5f]">{t('Ti ricontatteremo presto.')}</p>
                <Button
                  variant="outline"
                  onClick={() => setSubmitted(false)}
                  className="mt-6"
                >
                  {t('Invia un altro messaggio')}
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Input
                      name="name"
                      placeholder={t('Nome e Cognome')}
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-[#e5e5e5] focus:border-[#74675a] focus:ring-[#74675a]/20 
                        rounded-none h-12"
                    />
                  </div>
                  <div>
                    <Input
                      name="email"
                      type="email"
                      placeholder="E-mail"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-[#e5e5e5] focus:border-[#74675a] focus:ring-[#74675a]/20 
                        rounded-none h-12"
                    />
                  </div>
                </div>

                <Input
                  name="profession"
                  placeholder={t('Professione')}
                  value={formData.profession}
                  onChange={handleChange}
                  className="border-[#e5e5e5] focus:border-[#74675a] focus:ring-[#74675a]/20 
                    rounded-none h-12"
                />

                <Input
                  name="subject"
                  placeholder={t('Oggetto')}
                  value={formData.subject}
                  onChange={handleChange}
                  className="border-[#e5e5e5] focus:border-[#74675a] focus:ring-[#74675a]/20 
                    rounded-none h-12"
                />

                <Textarea
                  name="message"
                  placeholder={t('Messaggio')}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="border-[#e5e5e5] focus:border-[#74675a] focus:ring-[#74675a]/20 
                    rounded-none resize-none"
                />

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy"
                    checked={agreed}
                    onCheckedChange={setAgreed}
                    className="mt-0.5 border-[#e5e5e5] data-[state=checked]:bg-[#74675a] 
                      data-[state=checked]:border-[#74675a]"
                  />
                  <label htmlFor="privacy" className="text-sm text-[#5f5f5f] cursor-pointer">
                    {t('Acconsento al trattamento dei miei dati personali secondo la privacy policy del sito.')}
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={submitMutation.isPending}
                  className="bg-[#2d2d2d] hover:bg-[#1a1a1a] text-white rounded-none h-12 px-8
                    transition-colors duration-300"
                >
                  {submitMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('Invio in corso...')}
                    </>
                  ) : (
                    t('Invia')
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
      </div>
    </>
  );
}